import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { type JSX, lazy, Suspense, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";

import { RoutedErrorBoundary } from "@/components/ErrorBoundary";
import { PageTemplate } from "@/components/layout/PageTemplate";
import { SectionNav, type SectionNavItem } from "@/components/layout/SectionNav";
import { EmptyState, ErrorState, LoadingState, PageSkeleton } from "@/components/states";
import { StatusBadge, StatusDot } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import { Toaster } from "@/components/ui/sonner";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { AgentOutlet } from "@/lib/app-context";
import { EYEBROW } from "@/lib/class-presets";
import { sectionPathActive } from "@/lib/navigation";
import { agentPath, encoded } from "@/lib/paths";
import { cn } from "@/lib/utils";

import { ApiError, apiFetch } from "./api";
import type { AgentSummary, CurrentUser } from "./models";
import { ShellMetrics } from "./shell-metrics";
import { ShortcutsOverlay, useKeyboardShortcuts } from "./shortcuts";

/**
 * Routes are split per page: the shell, the sidebar and the shared blocks ship
 * in the entry chunk, and each page arrives when it is first visited. That
 * keeps recharts off the critical path for an operator who only opens Runs, and
 * `yaml` off it for everyone who never opens Config.
 */
const OverviewPage = lazy(async function loadOverviewPage() {
  return { default: (await import("./stats-ui")).OverviewPage };
});
const UsagePage = lazy(async function loadUsagePage() {
  return { default: (await import("./stats-ui")).UsagePage };
});
const AgentStatsPage = lazy(async function loadAgentStatsPage() {
  return { default: (await import("./stats-ui")).AgentStatsPage };
});
const AuditPage = lazy(async function loadAuditPage() {
  return { default: (await import("./audit-page")).AuditPage };
});
const GoogleOAuthCallbackPage = lazy(async function loadGoogleCallbackPage() {
  return { default: (await import("./google-accounts-page")).GoogleOAuthCallbackPage };
});
const LoginPage = lazy(async function loadLoginPage() {
  return { default: (await import("./pages/LoginPage")).LoginPage };
});
const AccountPage = lazy(async function loadAccountPage() {
  return { default: (await import("./pages/AccountPage")).AccountPage };
});
const UsersPage = lazy(async function loadUsersPage() {
  return { default: (await import("./pages/UsersPage")).UsersPage };
});
const SandboxImagesPage = lazy(async function loadSandboxImagesPage() {
  return { default: (await import("./pages/SandboxImagesPage")).SandboxImagesPage };
});
const ProposalsPage = lazy(async function loadProposalsPage() {
  return { default: (await import("./pages/ProposalsPage")).ProposalsPage };
});
const ArchiveProposalsPage = lazy(async function loadArchiveProposalsPage() {
  return { default: (await import("./pages/ArchiveProposalsPage")).ArchiveProposalsPage };
});
const RunsGlobalPage = lazy(async function loadRunsGlobalPage() {
  return { default: (await import("./pages/RunsGlobalPage")).RunsGlobalPage };
});
const SettingsLayout = lazy(async function loadSettingsLayout() {
  return { default: (await import("./pages/settings/SettingsLayout")).SettingsLayout };
});
const SettingsOverviewPage = lazy(async function loadSettingsOverviewPage() {
  return { default: (await import("./pages/settings/SettingsOverviewPage")).SettingsOverviewPage };
});
const SafetyPage = lazy(async function loadSafetyPage() {
  return { default: (await import("./pages/settings/platform-sections")).SafetyPage };
});
const CapacityPage = lazy(async function loadCapacityPage() {
  return { default: (await import("./pages/settings/platform-sections")).CapacityPage };
});
const AuthenticationPage = lazy(async function loadAuthenticationPage() {
  return { default: (await import("./pages/settings/platform-sections")).AuthenticationPage };
});
const NotificationsPage = lazy(async function loadNotificationsPage() {
  return { default: (await import("./pages/settings/platform-sections")).NotificationsPage };
});
const RetentionPage = lazy(async function loadRetentionPage() {
  return { default: (await import("./pages/settings/platform-sections")).RetentionPage };
});
const RejectedContentPage = lazy(async function loadRejectedContentPage() {
  return { default: (await import("./pages/settings/RejectedContentPage")).RejectedContentPage };
});
const ProvidersPage = lazy(async function loadProvidersPage() {
  return { default: (await import("./pages/settings/ProvidersPage")).ProvidersPage };
});
const SecretsPage = lazy(async function loadSecretsPage() {
  return { default: (await import("./pages/settings/SecretsPage")).SecretsPage };
});
const ConnectionsPage = lazy(async function loadConnectionsPage() {
  return { default: (await import("./pages/settings/ConnectionsPage")).ConnectionsPage };
});
const SystemStatusPage = lazy(async function loadSystemStatusPage() {
  return { default: (await import("./pages/settings/SystemStatusPage")).SystemStatusPage };
});
const ProjectsPage = lazy(async function loadProjectsPage() {
  return { default: (await import("./pages/projects/ProjectsPage")).ProjectsPage };
});
const ProjectDetailPage = lazy(async function loadProjectDetailPage() {
  return { default: (await import("./pages/projects/ProjectDetailPage")).ProjectDetailPage };
});
const AgentCreatePage = lazy(async function loadAgentCreatePage() {
  return { default: (await import("./pages/projects/AgentCreatePage")).AgentCreatePage };
});
const ChatPage = lazy(async function loadChatPage() {
  return { default: (await import("./pages/agent/ChatPage")).ChatPage };
});
const RunsPage = lazy(async function loadRunsPage() {
  return { default: (await import("./pages/agent/RunsPage")).RunsPage };
});
const SessionsPage = lazy(async function loadSessionsPage() {
  return { default: (await import("./pages/agent/SessionsPage")).SessionsPage };
});
const ConfigPage = lazy(async function loadConfigPage() {
  return { default: (await import("./pages/agent/ConfigPage")).ConfigPage };
});
const RunDetailPage = lazy(async function loadRunDetailPage() {
  return { default: (await import("./pages/agent/RunDetailPage")).RunDetailPage };
});

/**
 * Dev-only design-preview harness (ui-concept v1.0 section 10 stubs). The
 * inline `import.meta.env.DEV` ternary is folded at build time: in a production
 * build Vite replaces it with `false`, dead-code elimination drops the dynamic
 * import, and the preview chunk never ships. Verified by
 * scripts/design-screenshots.mjs's --check-bundle mode.
 */
const DesignPreviewRoutes = import.meta.env.DEV
  ? lazy(async function loadDesignPreviewRoutes() {
      return {
        default: (await import("./design-preview/DesignPreviewRoutes")).DesignPreviewRoutes,
      };
    })
  : null;

/**
 * The agent's tabs, in order. The routes keep their `/stats` and `/config`
 * spellings so every bookmark and runbook link still answers; only the labels
 * changed, to the words the breadcrumb and the pages themselves now use (§3).
 */
const AGENT_SECTIONS: readonly { readonly path: string; readonly label: string }[] = [
  { path: "chat", label: "Chat" },
  { path: "runs", label: "Runs" },
  { path: "sessions", label: "Sessions" },
  { path: "stats", label: "Usage & costs" },
  { path: "config", label: "Configuration" },
];

/** A tab owns its path and everything under it: run detail keeps Runs current. */
function agentSectionItems(base: string, pathname: string): readonly SectionNavItem[] {
  return AGENT_SECTIONS.map(function agentSection(section): SectionNavItem {
    const to = `${base}/${section.path}`;
    return { id: section.path, label: section.label, to, active: sectionPathActive(pathname, to) };
  });
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: 1, staleTime: 2_000, refetchOnWindowFocus: false },
      mutations: { retry: false },
    },
  });
}

/**
 * Suspense boundary for a lazily loaded route, keyed by the path it renders.
 *
 * The key is what makes a navigation commit immediately. React Router updates
 * router state inside `startTransition`, and React will not replace visible
 * content with a fallback during a transition — so when the next page's chunk
 * suspends, the browser URL changes while the committed tree, `useLocation()`
 * included, stays on the previous route until the chunk lands. Anything reading
 * the committed location in that window acts on the old path: the keyboard
 * shortcuts did exactly that, and Alt+W went nowhere right after a navigation.
 *
 * Re-keying mounts a fresh boundary, which React is free to show as a fallback
 * at once, so the location commits with the navigation and the page skeleton
 * covers the wait.
 */
export function RoutePane({
  children,
  routeKey,
}: {
  readonly children: JSX.Element;
  /** Overrides the path the boundary keys on; defaults to the full pathname. */
  readonly routeKey?: string;
}): JSX.Element {
  const location = useLocation();
  return (
    <Suspense fallback={<PageSkeleton />} key={routeKey ?? location.pathname}>
      {children}
    </Suspense>
  );
}

/**
 * The key for the shell's boundary. It stops at the agent, not the tab: the
 * agent layout — its header, its tab bar, its agent query — is shared by Chat,
 * Runs, Sessions, Stats and Config, so keying the shell on the full pathname
 * would tear all of that down and rebuild it on every tab click. The tab panel
 * has its own boundary inside `AgentLayout`, keyed on the full path, which is
 * what actually changes.
 */
function shellRouteKey(pathname: string): string {
  return /^\/agents\/[^/]+/u.exec(pathname)?.[0] ?? pathname;
}

/** Route wrappers read the shell identity inside the route, not eagerly. */
function ProjectsRoute(): JSX.Element {
  return <ProjectsPage me={useOutletContext<CurrentUser>()} />;
}

function ProjectDetailRoute(): JSX.Element {
  return <ProjectDetailPage me={useOutletContext<CurrentUser>()} />;
}

function AgentCreateRoute(): JSX.Element {
  return <AgentCreatePage me={useOutletContext<CurrentUser>()} />;
}

function BudgetPausedBadge(): JSX.Element {
  return <Badge variant="paused">Budget paused</Badge>;
}

function AppShell(): JSX.Element {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const shortcuts = useKeyboardShortcuts(navigate, location.pathname, me.data?.role === "admin");

  if (me.isLoading) return <LoadingState label="Loading account" />;
  if (me.isError) return <Navigate replace to="/login" />;
  if (me.data === undefined) return <LoadingState label="Loading account" />;
  const identity = me.data;
  return (
    <PageTemplate
      actions={
        <>
          <ShellMetrics />
          <Button
            aria-label="Keyboard shortcuts"
            className="font-semibold"
            onClick={shortcuts.openHelp}
            size="icon-sm"
            variant="outline"
          >
            ?
          </Button>
        </>
      }
      me={identity}
    >
      <RoutePane routeKey={shellRouteKey(location.pathname)}>
        <Outlet context={identity} />
      </RoutePane>
      <ShortcutsOverlay
        admin={identity.role === "admin"}
        onClose={shortcuts.closeHelp}
        open={shortcuts.helpOpen}
      />
    </PageTemplate>
  );
}

function AgentLayout(): JSX.Element {
  const me = useOutletContext<CurrentUser>();
  const { pathname } = useLocation();
  const params = useParams();
  const agentId = params.agentId ?? "";
  const agent = useQuery({
    queryKey: ["agents", agentId],
    queryFn: function fetchAgent(): Promise<AgentSummary> {
      return apiFetch(agentPath(agentId, ""));
    },
    enabled: agentId !== "",
  });
  function refetchAgent(): void {
    void agent.refetch();
  }
  if (agent.isLoading) return <LoadingState label="Loading agent" />;
  if (agent.isError && agent.error instanceof ApiError && agent.error.status === 403) {
    return <Navigate replace to="/" />;
  }
  if (agent.isError)
    return <ErrorState error={agent.error} onRetry={refetchAgent} retrying={agent.isFetching} />;
  if (agent.data === undefined)
    return <EmptyState title="Agent not found" detail="Choose another agent." />;
  const base = `/agents/${encoded(agentId)}`;
  return (
    <div className="grid gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className={EYEBROW}>{agent.data.role}</p>
          <h1 className="flex items-center gap-2">
            <StatusDot status={agent.data.enabled ? "enabled" : "disabled"} /> {agent.data.name}
          </h1>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{agent.data.model}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            The dot beside the name is decorative, so a disabled agent says so
            in words too — colour alone is not a status.
          */}
          {!agent.data.enabled && <StatusBadge status="disabled" />}
          {agent.data.budgetPaused && <BudgetPausedBadge />}
          <span className="border px-2 py-1 text-xs text-muted-foreground">
            rev {String(agent.data.revision)}
          </span>
        </div>
      </header>
      <SectionNav
        items={agentSectionItems(base, pathname)}
        label="Agent sections"
        variant="primary"
      />
      <RoutePane>
        <Outlet context={{ me, agent: agent.data } satisfies AgentOutlet} />
      </RoutePane>
    </div>
  );
}

function NotFoundPage(): JSX.Element {
  return (
    <div className="grid min-h-[60vh] content-center justify-items-start gap-3">
      <p className={EYEBROW}>404</p>
      <h1>Page not found</h1>
      <Link className={cn(buttonVariants())} to="/">
        Return home
      </Link>
    </div>
  );
}

/**
 * Continuity redirect for a renamed path: §3.5 moved four admin surfaces under
 * the Settings hub, and navigation concept v1.1 §8 renamed Proposals and
 * Archive proposals. Old links from runbooks and bookmarks must keep answering.
 *
 * Query *and* hash ride along, so deep links survive the hop whichever half
 * they live in — `?profile=` on sandbox images, audit filters, and the
 * `#api-access` style anchors the account menu now uses. `replace` keeps the
 * legacy path out of the history stack: Back has to leave the app rather than
 * bounce through the redirect again.
 */
function RedirectKeepingLocation({ to }: { readonly to: string }): JSX.Element {
  const location = useLocation();
  return <Navigate replace to={redirectTarget(to, location.search, location.hash)} />;
}

/**
 * The target with the incoming query merged in, not appended to it: a target
 * that names its own tab (`/settings/connections?tab=google`) would otherwise
 * produce `…?tab=google?foo=bar`, which is one query parameter called
 * `tab` whose value is `google?foo`. The target's own parameters win, because
 * they are what makes the hop land on the right screen.
 */
function redirectTarget(to: string, search: string, hash: string): string {
  const [path, ownQuery] = to.split("?");
  if (ownQuery === undefined || path === undefined) return `${to}${search}${hash}`;
  const merged = new URLSearchParams(ownQuery);
  for (const [key, value] of new URLSearchParams(search)) {
    if (!merged.has(key)) merged.append(key, value);
  }
  return `${path}?${merged.toString()}${hash}`;
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route
        element={
          <RoutePane>
            <LoginPage />
          </RoutePane>
        }
        path="/login"
      />
      {DesignPreviewRoutes !== null && (
        <Route element={<DesignPreviewRoutes />} path="/__design/*" />
      )}
      <Route element={<AppShell />} path="/">
        <Route index element={<OverviewPage />} />
        <Route element={<AccountPage />} path="account" />
        {/*
          Canonical monitoring destinations (§8). The Action log renders the
          existing integration screen; its mode/outcome pass is a later slice.
        */}
        <Route element={<ProposalsPage />} path="action-log" />
        <Route element={<UsagePage />} path="usage" />
        <Route element={<RunsGlobalPage />} path="runs" />
        <Route element={<SettingsLayout />} path="settings">
          <Route element={<SettingsOverviewPage />} index />
          <Route element={<ProvidersPage />} path="providers" />
          <Route element={<SecretsPage />} path="secrets" />
          {/*
            Data sources and Google accounts are Connections TABS now (§3).
            Their own paths keep answering by rendering the matching tab rather
            than redirecting, so a bookmark lands on the screen it named.
          */}
          <Route element={<ConnectionsPage initialTab="data-sources" />} path="data-sources" />
          <Route element={<ConnectionsPage />} path="connections" />
          <Route element={<ConnectionsPage initialTab="google" />} path="connections/google" />
          <Route element={<UsersPage />} path="users" />
          <Route element={<SandboxImagesPage />} path="sandbox-images" />
          <Route element={<AuditPage />} path="audit" />
          <Route element={<SafetyPage />} path="safety" />
          <Route element={<CapacityPage />} path="capacity" />
          <Route element={<AuthenticationPage />} path="authentication" />
          <Route element={<NotificationsPage />} path="notifications" />
          <Route element={<RetentionPage />} path="retention" />
          <Route element={<RejectedContentPage />} path="rejected-content" />
          <Route element={<SystemStatusPage />} path="system-status" />
          {/* Archive requests belong to Maintenance, never beside Action log (§5). */}
          <Route element={<ArchiveProposalsPage />} path="maintenance/archive-requests" />
        </Route>
        {/*
          The OAuth return hop keeps its exact path: it is the redirect target
          registered in Google, so §3.5 renames must never touch it.
        */}
        <Route element={<GoogleOAuthCallbackPage />} path="integrations/google/callback" />
        <Route element={<RedirectKeepingLocation to="/settings/users" />} path="users" />
        <Route
          element={<RedirectKeepingLocation to="/settings/sandbox-images" />}
          path="sandbox-images"
        />
        <Route
          element={<RedirectKeepingLocation to="/settings/connections?tab=google" />}
          path="integrations/google"
        />
        <Route element={<RedirectKeepingLocation to="/settings/audit" />} path="audit" />
        {/* The v1.1 renames keep their old paths answering (§8). */}
        <Route element={<RedirectKeepingLocation to="/action-log" />} path="proposals" />
        <Route
          element={<RedirectKeepingLocation to="/settings/maintenance/archive-requests" />}
          path="archive-proposals"
        />
        <Route element={<ProjectsRoute />} path="projects" />
        <Route element={<ProjectDetailRoute />} path="projects/:projectId/*" />
        <Route element={<AgentCreateRoute />} path="projects/:projectId/agents/new" />
        <Route element={<AgentLayout />} path="agents/:agentId">
          <Route index element={<Navigate replace to="chat" />} />
          <Route element={<ChatPage />} path="chat" />
          <Route element={<RunsPage />} path="runs" />
          <Route element={<SessionsPage />} path="sessions" />
          <Route element={<AgentStatsPage />} path="stats" />
          <Route element={<ConfigPage />} path="config" />
          <Route element={<RunDetailPage />} path="runs/:runUid" />
        </Route>
        <Route element={<NotFoundPage />} path="*" />
      </Route>
    </Routes>
  );
}

export function App(): JSX.Element {
  const [queryClient] = useState(createAppQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RoutedErrorBoundary>
          <AppRoutes />
        </RoutedErrorBoundary>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
